import { markRaw, ref, shallowRef } from 'vue';
import { Canvas, Control, controlsUtils, FabricImage, util } from 'fabric';
import * as pdfjs from 'pdfjs-dist';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker?worker';

import SignatureRegionImage from '@/assets/images/signatures/signature_region.png';

import {
  DEFAULT_CORNER_SIZE,
  MIN_SIGNATURE_WIDTH,
  OPACITY_DISABLED,
  OPACITY_ENABLED,
  PDF_RENDER_SCALE,
  SIGNATURE_ICONS,
  STYLE_COLORS,
} from '@/app/data/signature_constants.js';

window.pdfjsWorker = pdfjsWorker;
pdfjs.GlobalWorkerOptions.workerSrc = 'https://public.cdn.ziggu.app/static/js/pdf.worker.js';

function renderIcon(iconUrl) {
  const img = new Image();
  img.src = iconUrl;
  return function iconRender(ctx, left, top, _styleOverride, fabricObject) {
    const size = fabricObject.cornerSize;
    ctx.save();
    ctx.translate(left, top);
    ctx.rotate(util.degreesToRadians(fabricObject.angle));
    ctx.drawImage(img, -size / 2, -size / 2, size, size);
    ctx.restore();
  };
}

const useSignatureCanvas = () => {
  const fabricCanvasInstance = shallowRef(null);
  const page = ref(1);
  const pdfPagesCount = ref(0);
  const isLoading = ref(false);
  const errorMsg = ref(false);

  const docDimensions = ref({ width: 0, height: 0, factorX: 1, factorY: 1 });

  const setupFabric = (canvasId = 'canvas') => {
    fabricCanvasInstance.value = markRaw(new Canvas(canvasId));
  };

  const renderDocument = async (url, pageNumber, onCanvasReady) => {
    isLoading.value = true;
    errorMsg.value = false;
    page.value = pageNumber;

    try {
      const pageUrl = await getPdfPageAsImage(url, pageNumber);
      await buildCanvasBackground(pageUrl, onCanvasReady);
    } catch (error) {
      if (error.name === 'InvalidPDFException' || error.message.includes('Invalid PDF structure')) {
        await buildCanvasBackground(url, onCanvasReady);
      } else {
        errorMsg.value = 'Could not load document. Please check if the file is valid.';
      }
    } finally {
      isLoading.value = false;
    }
  };

  const getPdfPageAsImage = async (url, pageNumber) => {
    const loadingTask = pdfjs.getDocument({
      url: url,
      cMapUrl: '/cmaps/',
      cMapPacked: true,
    });

    const pdf = await loadingTask.promise;
    pdfPagesCount.value = pdf.numPages;

    const pdfPage = await pdf.getPage(pageNumber);
    const viewport = pdfPage.getViewport({ scale: PDF_RENDER_SCALE });

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.height = viewport.height;
    canvas.width = viewport.width;

    await pdfPage.render({ canvasContext: context, viewport }).promise;
    return canvas.toDataURL();
  };

  const buildCanvasBackground = (fileUrl, onCanvasReady) => {
    if (!fabricCanvasInstance.value) return;

    const canvasModal = document.getElementById('canvas-modal');
    if (!canvasModal) {
      return;
    }

    const parentContainer = canvasModal.parentElement;
    const modalWidth = parentContainer.offsetWidth || canvasModal.offsetWidth;
    const maxHeight = window.innerHeight;

    const doc = new Image();
    doc.crossOrigin = 'Anonymous';

    return new Promise((resolve, reject) => {
      doc.onload = () => {
        const ratio = Math.min(modalWidth / doc.width, maxHeight / doc.height);
        const width = doc.width * ratio;
        const height = doc.height * ratio;

        canvasModal.style.width = `${width}px`;
        const animContent = document.getElementsByClassName('animation-content')[0];
        if (animContent) animContent.style.width = `${width}px`;
        docDimensions.value = {
          width: width,
          height: height,
          factorX: width / doc.width,
          factorY: height / doc.height,
        };

        const canvas = fabricCanvasInstance.value;
        canvas.setWidth(width);
        canvas.setHeight(height);

        FabricImage.fromURL(doc.src).then((bgImg) => {
          bgImg.set({ scaleX: docDimensions.value.factorX, scaleY: docDimensions.value.factorY });
          canvas.backgroundImage = bgImg;
          canvas.requestRenderAll();
          if (onCanvasReady) onCanvasReady();
          resolve();
          return null;
        });
      };

      doc.onerror = (e) => {
        errorMsg.value = 'Failed to load document image data.';
        reject(e);
      };

      doc.src = fileUrl;
    });
  };

  const createSignatureRect = async (options, handlers) => {
    const { left, top, width, height, signatoryType, isSelectable = true } = options;
    const { onDelete, onUpdate } = handlers;

    const imgObj = await FabricImage.fromURL(SignatureRegionImage);

    const scaleX = width / imgObj.width;
    const scaleY = height / imgObj.height;

    imgObj.set({
      left,
      top,
      scaleX,
      scaleY,
      hasRotatingPoint: false,
      minScaleLimit: MIN_SIGNATURE_WIDTH / imgObj.width,
      borderColor: STYLE_COLORS.BORDER_TRANSPARENT,
      opacity: isSelectable ? OPACITY_ENABLED : OPACITY_DISABLED,
      selectable: isSelectable,
      transparentCorners: false,
      cornerSize: DEFAULT_CORNER_SIZE,
    });

    imgObj.signatory_type = signatoryType;

    const deleteControl = new Control({
      x: 0.5,
      y: -0.5,
      cursorStyle: 'pointer',
      mouseUpHandler: (eventData, transform) => {
        const target = transform.target;
        fabricCanvasInstance.value.remove(target);
        if (onDelete) onDelete();
        return true;
      },
      render: renderIcon(SIGNATURE_ICONS.DELETE),
    });

    const createResizeControl = (x, y, cursorStyle) =>
      new Control({
        x,
        y,
        cursorStyle,
        render: renderIcon(SIGNATURE_ICONS.CORNER),
        actionHandler: controlsUtils.scalingEqually,
      });

    imgObj.controls = {
      tr: deleteControl,
      tl: createResizeControl(-0.5, -0.5, 'nwse-resize'),
      bl: createResizeControl(-0.5, 0.5, 'nesw-resize'),
      br: createResizeControl(0.5, 0.5, 'nwse-resize'),
    };

    imgObj.on('scaling', function handleScaling() {
      if (onUpdate) {
        onUpdate(this);
      }
    });
    imgObj.on('modified', function handleModified() {
      if (onUpdate) {
        onUpdate(this);
      }
    });

    fabricCanvasInstance.value.add(imgObj);
    return imgObj;
  };

  return {
    fabricCanvasInstance,
    page,
    pdfPagesCount,
    isLoading,
    errorMsg,
    docDimensions,
    setupFabric,
    renderDocument,
    createSignatureRect,
  };
};

export default useSignatureCanvas;
